/**
 * Rebuild the Wall — Nehemiah chapter story content (option B reskin).
 * Combat scripts live in missions.ts; this is narrative only.
 *
 * artSrc paths ship via: npm run story-art -- ship <panel-id>
 * → public/art/story/neh-wall/<panel-id>.webp
 */
import type { StageStory } from "./types";

export const CAMPAIGN_CHAPTER = {
  id: "neh-wall",
  title: "Rebuild the Wall",
  kicker: "Single-player · Proclaim · Nehemiah",
  lead: "Eight stages through the Book of Nehemiah. Each battle is a doubt, a mockery, or a weariness the wall-builders faced — answered with Scripture.",
  attribution: "Based on the Book of Nehemiah (KJV).",
  /** Always granted on first clear of the finale (any difficulty). */
  signatureVerseId: "neh-8-10",
  finaleMissionId: "neh-joy-of-the-lord",
} as const;

export const STORY_BEFORE_THE_KING: StageStory = {
  title: "Before the King",
  scriptureRef: "Nehemiah 1:1–2:8",
  lie: "You're only a servant. If you speak, you'll lose everything — and nothing will change.",
  reflection: "Fear spoke first. Trust answered out loud.",
  intro: [
    {
      caption: "The report",
      artSrc: "neh-wall/n1-report.webp",
      lines: [
        "The city of his fathers lay open and ashamed — walls broken, gates burned with fire.",
        "Nehemiah sat down and wept, and mourned, and fasted, and prayed before the God of heaven.",
      ],
    },
    {
      caption: "The cupbearer",
      artSrc: "neh-wall/n1-cupbearer.webp",
      lines: [
        "In the presence of the king he carried the cup — and a sadness he could not hide.",
        "When the king asked why his face was sad, fear rose like a tide. Still he spoke the request God had put in his heart.",
      ],
    },
  ],
  outro: [
    {
      caption: "Favor",
      artSrc: "neh-wall/n1-favor.webp",
      lines: [
        "The king said yes. Letters. Timber. A road home.",
        "Favor does not cancel fear — it calls you to walk through it.",
      ],
    },
  ],
};

export const STORY_NIGHT_SURVEY: StageStory = {
  title: "The Night Survey",
  scriptureRef: "Nehemiah 2:9–16",
  lie: "If you really look, you'll despair. Better not to know how bad it is.",
  reflection: "Honesty is not hopelessness. God can rebuild what you dare to see.",
  intro: [
    {
      caption: "By night",
      artSrc: "neh-wall/n2-by-night.webp",
      lines: [
        "He arrived and rested three days. Then he went out by night with a few men.",
        "He told no one yet what God had put in his heart to do at Jerusalem.",
      ],
    },
    {
      caption: "The ruin",
      artSrc: "neh-wall/n2-ruin.webp",
      lines: [
        "No speech. No rally. Only broken stones and scorched gates.",
        "He measured the damage in silence so that when he spoke, he would speak true.",
      ],
    },
  ],
  outro: [
    {
      caption: "Ruin and call",
      artSrc: "neh-wall/n2-both.webp",
      lines: [
        "The ruin was real. So was the call.",
        "Seeing both is the beginning of building.",
      ],
    },
  ],
};

export const STORY_RISE_AND_BUILD: StageStory = {
  title: "Rise and Build",
  scriptureRef: "Nehemiah 2:17–20",
  lie: "Even if God is with you, the work is too heavy. You'll start and fail — and everyone will see.",
  reflection: 'Mockery is loud. "Let us rise" is louder when God has spoken.',
  intro: [
    {
      caption: "The call",
      artSrc: "neh-wall/n3-call.webp",
      lines: [
        "He named the distress out loud — then the remedy.",
        '"Come, and let us build up the wall of Jerusalem." Their hands strengthened for this good work.',
      ],
    },
    {
      caption: "The scorn",
      artSrc: "neh-wall/n3-scorn.webp",
      lines: [
        "Sanballat, Tobiah, and Geshem laughed them to scorn and despised them.",
        "Nehemiah answered: the God of heaven, He will prosper us; therefore we His servants will arise and build.",
      ],
    },
  ],
  outro: [
    {
      caption: "We build",
      artSrc: "neh-wall/n3-prosper.webp",
      lines: [
        "Building begins under open scorn.",
        "The God of heaven still prospers the work.",
      ],
    },
  ],
};

export const STORY_MOCKERY: StageStory = {
  title: "Mockery from Outside",
  scriptureRef: "Nehemiah 4:1–6",
  lie: "They're right. You're weak. One hard push and the whole thing falls.",
  reflection: "Insults aim at the Heart. Peace and faith answer on the layer they strike.",
  intro: [
    {
      caption: "Fury on the ridge",
      artSrc: "neh-wall/n4-fury.webp",
      lines: [
        "Sanballat was wroth and mocked the Jews. Tobiah said even a fox would break their stone wall.",
        "They prayed — and set their hearts to work.",
      ],
    },
    {
      caption: "A mind to work",
      artSrc: "neh-wall/n4-mind-to-work.webp",
      lines: [
        "So built we the wall; and all the wall was joined together unto the half thereof:",
        "for the people had a mind to work.",
      ],
    },
  ],
  outro: [
    {
      caption: "Keep building",
      artSrc: "neh-wall/n4-keep-building.webp",
      lines: [
        "A mind to work is a kind of warfare.",
        "Keep building while they talk.",
      ],
    },
  ],
};

export const STORY_SCHEMES: StageStory = {
  title: "Schemes and Letters",
  scriptureRef: "Nehemiah 4:7–15; 6:1–14",
  lie: "Come down and talk — stall the work. Or hide. Or believe the rumor that you only want a crown.",
  reflection: 'Not every invitation is peace. Some "peace talks" are traps for the wall.',
  intro: [
    {
      caption: "Conspiracy",
      artSrc: "neh-wall/n5-conspiracy.webp",
      lines: [
        "When the enemies heard that the walls were made up, they conspired to fight Jerusalem.",
        "Nevertheless we made our prayer unto our God, and set a watch against them day and night.",
      ],
    },
    {
      caption: "Come down",
      artSrc: "neh-wall/n5-come-down.webp",
      lines: [
        "They sent to him: Come, let us meet together in the plain of Ono — but they thought to do him mischief.",
        'He answered: I am doing a great work, so that I cannot come down. Should such a man as I flee?',
      ],
    },
  ],
  outro: [
    {
      caption: "Stay on the wall",
      artSrc: "neh-wall/n5-stay.webp",
      lines: [
        "Discernment is courage with its eyes open.",
        "Stay on the wall.",
      ],
    },
  ],
};

export const STORY_STRENGTH_DECAYED: StageStory = {
  title: "Strength Decayed",
  scriptureRef: "Nehemiah 4:10–23; 5:1–13",
  lie: "You're too tired. Your own people are against you. Quit before you break.",
  reflection: "Weariness is real. So is the God who renews strength mid-build.",
  intro: [
    {
      caption: "Rubbish and fear",
      artSrc: "neh-wall/n6-rubbish.webp",
      lines: [
        '"The strength of the bearers of burdens is decayed, and there is much rubbish."',
        "Enemies threatened a surprise attack. Within the camp, outcry and exhaustion rose among their own.",
      ],
    },
    {
      caption: "Sword and labor",
      artSrc: "neh-wall/n6-sword-labor.webp",
      lines: [
        "Half of them held spears from the rising of the morning till the stars appeared.",
        "Everyone with one of his hands wrought in the work, and with the other hand held a weapon.",
      ],
    },
  ],
  outro: [
    {
      caption: "Kept",
      artSrc: "neh-wall/n6-kept.webp",
      lines: [
        "God does not despise tired builders.",
        "He arms them and keeps them.",
      ],
    },
  ],
};

export const STORY_SWORD_AND_TROWEL: StageStory = {
  title: "Sword and Trowel",
  scriptureRef: "Nehemiah 4:16–23; 6:15–16",
  lie: "You can't build and fight at once. Pick one — or lose both.",
  reflection: "The Word in one hand, obedience in the other. The wall rises.",
  intro: [
    {
      caption: "Both callings",
      artSrc: "neh-wall/n7-both.webp",
      lines: [
        "They that builded on the wall, and they that bare burdens, with those that laded —",
        "every one with one of his hands wrought in the work, and with the other hand held a weapon.",
      ],
    },
    {
      caption: "Finished",
      artSrc: "neh-wall/n7-finished.webp",
      lines: [
        "So the wall was finished in the twenty and fifth day of the month Elul, in fifty and two days.",
        "When the enemies heard, they were much cast down: for they perceived that this work was wrought of our God.",
      ],
    },
  ],
  outro: [
    {
      caption: "Of our God",
      artSrc: "neh-wall/n7-of-god.webp",
      lines: [
        "When the wall stood, even the enemies knew:",
        "this was wrought of our God.",
      ],
    },
  ],
};

export const STORY_JOY_OF_THE_LORD: StageStory = {
  title: "Joy of the Lord",
  scriptureRef: "Nehemiah 8:1–12",
  lie: "After all that, you should only feel solemn and small. Joy would be irresponsible.",
  reflection:
    "The wall stands. Strength for what remains is joy in the Lord — not grim survival alone.",
  intro: [
    {
      caption: "The Law read",
      artSrc: "neh-wall/n8-law.webp",
      lines: [
        "All the people gathered as one man. Ezra the scribe brought the book of the law.",
        "They heard the words and wept.",
      ],
    },
    {
      caption: "Strength",
      artSrc: "neh-wall/n8-strength.webp",
      lines: [
        "Then he said unto them, Go your way, eat the fat, and drink the sweet, and send portions —",
        "for this day is holy unto our Lord: neither be ye sorry; for the joy of the Lord is your strength.",
      ],
    },
  ],
  outro: [
    {
      caption: "Returned",
      artSrc: "neh-wall/n8-returned.webp",
      lines: [
        "Rebuilding was never only stone.",
        "It was a people returned to God's joy.",
      ],
    },
  ],
};
